<template>
  <v-container>
    <v-row class="mx-0">
      <v-col cols="12" md="9" order="1" order-md="0" class="px-2 mb-2 mb-md-0">
        <v-select
          v-if="inactiveView !== 'subtitle' && selectedLang !== ''"
          v-model="selectedLang"
          :items="availableLanguages"
          item-title="languageTranslation"
          item-value="languageCode"
          rounded="false"
          hide-details
          single-line
          :label="t(l.choose_language)"
          class="w-50 mb-5"
        >
        </v-select>
        <h3 v-if="inactiveView === 'easyToRead'" class="mb-8">{{ t(l.film.subtitle) }}</h3>
        <h3 v-else class="mb-8">{{ t(l.film.easyToRead) }}</h3>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div :class="inactiveView === 'subtitle' ? 'easy-to-read' : ''" v-html="getText" />
      </v-col>
      <v-col
        v-if="getMarketLanguage() === 'sv'"
        cols="12"
        md="3"
        class="d-flex align-top justify-md-end px-2 pr-md-0 pl-sm-3"
      >
        <PlayNarrationButton
          v-if="inactiveView === 'subtitle' && audioData"
          :value="[audioData]"
          :button-styled="true"
          :pausing="true"
        />
        <v-btn
          :disabled="!video.summary.length"
          class="text-none"
          variant="outlined"
          color="secondary"
          @click="clickSwitchView"
        >
          {{ t(l.film[inactiveView]) }}
          <FontAwesomeIcon size="sm" class="ml-2" icon="fa-solid fa-right-left" />
        </v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import axios from 'axios';
import { stopAllSounds } from '@ilteducation/audio-resources';
import config from '@/config/ConfigProvider';
import useLanguagesStore from '@/store/languages';
import useLanguageTranslations from '@/composables/use-language-translations';
import type { Video } from '@ilteducation/videos';
import { computed, onMounted, ref, watch } from 'vue';
import useAudioStore from '@/store/audio';
import { useLocale } from '@/plugins/translation';
import { storeToRefs } from 'pinia';
import { useCategories } from '@ilteducation/content-ref-data';
import { useRoute } from 'vue-router';
import { useVideos } from '@ilteducation/videos';
import PlayNarrationButton from '../base/PlayNarrationButton.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { trackTabAction } from '@/composables/use-video-tab-analytics';
const { getMarketLanguage } = useLanguagesStore();
const { populateLanguage, languageTranslated } = useLanguageTranslations();
const { t, l } = useLocale();

const props = defineProps<{ video: Video }>();
const audioStore = useAudioStore();
const videoStore = useVideos();
const { addAudioResources } = audioStore;
const { audioResources } = storeToRefs(audioStore);
const route = useRoute();
const { categories } = useCategories();
const currentSubjectId = categories.value.find((cat) => cat.slug === route.params.subject)?.id;

const selectedLang = ref('');
const subtitleText = ref('');
const inactiveView = ref<'subtitle' | 'easyToRead'>('subtitle');
const signedUrl = ref('');

const getText = computed(() => (inactiveView.value === 'easyToRead' ? subtitleText.value : props.video.summary));
const audioData = computed(
  () =>
    currentSubjectId &&
    audioResources.value.find(
      (audio) => audio.key === `${props.video.introfilmsUids[currentSubjectId]}_plain_language_summary`,
    ),
);
const availableLanguages = computed(() => props.video.subtitles.map(populateLanguage).filter(languageTranslated));

const prettifyText = (textInput: string) => {
  let textOutput = textInput.replaceAll(/^([0-9]+\n|)([0-9:,->\s]+^)/gm, '');
  textOutput = textOutput.replace('WEBVTT', '');
  textOutput = textOutput.replace('...', ' ');
  textOutput = textOutput.replace('..', ' ');
  textOutput = textOutput.replace(/([a-zA-Z-,–]\r\n|[a-zA-Z-,–]\n|[a-zA-Z-,–]\r)/g, (_match, p1) => `${p1.trim()} `);
  textOutput = textOutput.replace(/(\r\n|\n|\r)/g, '<br>');
  textOutput = textOutput.replace(/<br>/, '');
  return textOutput;
};

const getSummaryAudio = () => {
  const refName = currentSubjectId ? `${props.video.introfilmsUids[currentSubjectId]}_plain_language_summary` : '';
  if (refName && !audioData.value) {
    axios
      .get(`${config.value('audioCentralUrl')}?ref=${encodeURIComponent(refName)}&language=swe&recType=voice`)
      .then((res) => {
        if (res.data && res.data[0] && res.data[0].id) {
          const { id } = res.data[0];
          axios.get(`${config.value('contentRegistryUrl')}${res.data[0].id}/contentUrls`).then((contentRes) => {
            addAudioResources({
              url: contentRes.data.url,
              key: refName,
              id,
              lang: res.data[0].language === 'swe' ? 'sv' : res.data[0].language,
            });
          });
        }
      });
  }
};

const fetchSubtitles = async (lang: string) => {
  const subtitlesUrl = props.video.subtitles.find((la) => la.languageCode === lang)?.value?.fileName;
  if (subtitlesUrl) {
    signedUrl.value = await videoStore.getDownloadUrl(props.video.id);
    const res = await axios.get(`${signedUrl.value}/${subtitlesUrl}`, {
      noAuth: true,
    });
    subtitleText.value = prettifyText(res.data);
  }
};

const switchView = () => {
  stopAllSounds();
  inactiveView.value = inactiveView.value === 'easyToRead' ? 'subtitle' : 'easyToRead';
  if (inactiveView.value === 'subtitle' && !audioData.value) {
    getSummaryAudio();
  }
};

const clickSwitchView = () => {
  switchView();
  trackTabAction('subtitles', inactiveView.value === 'easyToRead' ? 'open_transcript' : 'open_easy_to_read');
};

watch(
  [selectedLang, () => props.video],
  ([lang, video]) => {
    if (video && lang) {
      fetchSubtitles(lang);
    }
  },
  { immediate: true },
);

onMounted(() => {
  if (props.video.subtitles.find((item) => item.languageCode === getMarketLanguage())) {
    selectedLang.value = getMarketLanguage();
  } else if (props.video.subtitles.length > 0) {
    selectedLang.value = props.video.subtitles[0].languageCode;
  }

  if (!props.video.summary.length) {
    switchView();
  } else if (!audioData.value) {
    getSummaryAudio();
  }
});
</script>

<style scoped lang="scss">
.easy-to-read {
  max-width: 47ch;
}
:deep(.v-select) {
  .v-input__slot {
    border: 1px solid transparent;
  }
  &.v-input--is-focused {
    .v-input__slot {
      border-color: var(--v-secondary-base);
    }
  }
}
</style>
